/* ═══════════════════════════════════════════════════════════════════
   RFW LANDING VIVA — Mock Data
   BLOQUE 7 DE 10 — Landing Viva
   ═══════════════════════════════════════════════════════════════════ */

import { liveRadios, liveRadiosStats } from './liveRadios';
import { livePrograms } from './livePrograms';
import { liveHosts } from './liveHosts';
import { getAllClubs, clubsSummary } from './rfwClubs';
import { getActiveCompetitions, getRecentWinners } from './rfwCompetitions';
import { getTodayEvents, getUpcomingEvents } from './globalEvents';
import { trendingItems, trendingSummary } from './trendingRfw';
import { getRadioRanking } from './rfwScores';

/* ─── Live Counter ─── */
export interface LiveCounter {
  id: string;
  label: string;
  value: number;
  suffix: string;
  icon: string;
  color: string;
  pulseColor: string;
}

export const liveCounters: LiveCounter[] = [
  {
    id: 'radios',
    label: 'Radios',
    value: 82,
    suffix: '',
    icon: 'ri-radio-line',
    color: 'text-rose-500',
    pulseColor: 'bg-rose-400',
  },
  {
    id: 'oyentes',
    label: 'Oyentes',
    value: liveRadiosStats.totalLiveListeners,
    suffix: '',
    icon: 'ri-headphone-line',
    color: 'text-emerald-500',
    pulseColor: 'bg-emerald-400',
  },
  {
    id: 'locutores',
    label: 'Locutores',
    value: 168,
    suffix: '',
    icon: 'ri-mic-line',
    color: 'text-amber-500',
    pulseColor: 'bg-amber-400',
  },
  {
    id: 'programas',
    label: 'Programas',
    value: 64,
    suffix: '',
    icon: 'ri-calendar-check-line',
    color: 'text-sky-500',
    pulseColor: 'bg-sky-400',
  },
  {
    id: 'clubes',
    label: 'Clubes',
    value: clubsSummary.totalClubs,
    suffix: '',
    icon: 'ri-vip-crown-line',
    color: 'text-purple-500',
    pulseColor: 'bg-purple-400',
  },
  {
    id: 'concursos',
    label: 'Concursos',
    value: 18,
    suffix: '',
    icon: 'ri-trophy-line',
    color: 'text-orange-500',
    pulseColor: 'bg-orange-400',
  },
  {
    id: 'eventos',
    label: 'Eventos',
    value: clubsSummary.totalEvents,
    suffix: '',
    icon: 'ri-calendar-event-line',
    color: 'text-teal-500',
    pulseColor: 'bg-teal-400',
  },
];

/* ─── Live Highlights ─── */
export interface LiveHighlight {
  id: number;
  type: 'radio' | 'programa' | 'locutor';
  name: string;
  subtitle: string;
  thumbnailUrl: string;
  statusLabel: string;
  statusColor: string;
  pulseColor: string;
  metricLabel: string;
  metricValue: number;
  linkUrl: string;
}

export const liveHighlights: LiveHighlight[] = [
  ...liveRadios.slice(0, 6).map((radio) => ({
    id: radio.id,
    type: 'radio' as const,
    name: radio.name,
    subtitle: radio.frequency,
    thumbnailUrl: radio.thumbnailUrl,
    statusLabel: radio.status === 'live-event' ? 'EVENTO LIVE' : radio.status === 'live-show' ? 'SHOW LIVE' : radio.status === 'special' ? 'ESPECIAL' : 'AL AIRE',
    statusColor: radio.status === 'live-event' ? 'bg-fuchsia-500' : radio.status === 'live-show' ? 'bg-purple-500' : radio.status === 'special' ? 'bg-amber-500' : 'bg-rose-500',
    pulseColor: 'bg-rose-400',
    metricLabel: 'oyentes',
    metricValue: radio.currentListeners,
    linkUrl: '/radio/' + radio.frequency.replace(/[.\s]+FM$/g, '').replace(/\./g, '-'),
  })),
  ...livePrograms.slice(0, 3).map((prog) => ({
    id: prog.id + 100,
    type: 'programa' as const,
    name: prog.name,
    subtitle: prog.radioName + ' · ' + prog.radioFrequency,
    thumbnailUrl: prog.coverUrl,
    statusLabel: 'AL AIRE',
    statusColor: 'bg-emerald-500',
    pulseColor: 'bg-emerald-400',
    metricLabel: 'seguidores',
    metricValue: prog.followers,
    linkUrl: '/program/' + prog.slug,
  })),
  ...liveHosts.slice(0, 3).map((host) => ({
    id: host.id + 200,
    type: 'locutor' as const,
    name: host.name,
    subtitle: host.radioName + ' · ' + host.radioFrequency,
    thumbnailUrl: host.photoUrl,
    statusLabel: 'EN CABINA',
    statusColor: 'bg-amber-500',
    pulseColor: 'bg-amber-400',
    metricLabel: 'seguidores',
    metricValue: host.followers,
    linkUrl: '/host/' + host.name.toLowerCase().replace(/\s+/g, '-'),
  })),
];

/* ─── Trend Period ─── */
export type TrendPeriod = 'hoy' | 'semana' | 'mes';

export const trendPeriodLabels: Record<TrendPeriod, string> = {
  hoy: 'Hoy',
  semana: 'Semana',
  mes: 'Mes',
};

/* ─── Top Rankings (compact for landing) ─── */
export interface LandingRankingEntry {
  position: number;
  name: string;
  subtitle: string;
  logoUrl: string;
  score: number;
  trend: 'up' | 'down' | 'stable';
  trendValue: number;
}

export function getTopRadiosForLanding(): LandingRankingEntry[] {
  return getRadioRanking().slice(0, 5).map((entry) => ({
    position: entry.position,
    name: entry.entityName,
    subtitle: entry.entitySubtitle,
    logoUrl: entry.logoUrl,
    score: entry.score,
    trend: (entry.trend === 'exploding' || entry.trend === 'growing' || entry.trend === 'trending') ? 'up' : entry.trend === 'declining' ? 'down' : 'stable',
    trendValue: entry.scoreChange,
  }));
}

/* ─── Landing Stats Summary ─── */
export interface LandingVivaSummary {
  radiosEnVivo: number;
  programasEnVivo: number;
  locutoresEnVivo: number;
  oyentesConectados: number;
  clubesActivos: number;
  concursosActivos: number;
  eventosHoy: number;
  tendenciasActivas: number;
  interaccionesHoy: number;
}

export const landingVivaSummary: LandingVivaSummary = {
  radiosEnVivo: liveRadios.length,
  programasEnVivo: livePrograms.length,
  locutoresEnVivo: liveHosts.length,
  oyentesConectados: liveRadiosStats.totalLiveListeners,
  clubesActivos: clubsSummary.totalActiveChallenges,
  concursosActivos: 5,
  eventosHoy: getTodayEvents().length,
  tendenciasActivas: trendingSummary.totalTrending,
  interaccionesHoy: 142860,
};

/* ─── Quick CTAs ─── */
export interface LandingCTA {
  id: string;
  label: string;
  subtitle: string;
  icon: string;
  bgClass: string;
  textClass: string;
  linkUrl: string;
}

export const landingCTAs: LandingCTA[] = [
  {
    id: 'cabina',
    label: 'Entrar a Cabina',
    subtitle: 'Chatea en vivo con tu radio favorita',
    icon: 'ri-chat-smile-2-line',
    bgClass: 'bg-rose-500 hover:bg-rose-600',
    textClass: 'text-background-50',
    linkUrl: '/radios-en-vivo',
  },
  {
    id: 'seguir',
    label: 'Seguir Radio',
    subtitle: 'Descubre y sigue nuevas emisoras',
    icon: 'ri-heart-add-line',
    bgClass: 'bg-accent-500 hover:bg-accent-600',
    textClass: 'text-background-50',
    linkUrl: '/explorar',
  },
  {
    id: 'club',
    label: 'Unirse al Club',
    subtitle: 'Forma parte de la comunidad más grande',
    icon: 'ri-vip-crown-line',
    bgClass: 'bg-amber-500 hover:bg-amber-600',
    textClass: 'text-background-50',
    linkUrl: '/clubes',
  },
];

/* ─── Activity Feed (últimas acciones en la plataforma) ─── */
export interface ActivityFeedItem {
  id: number;
  text: string;
  icon: string;
  iconColor: string;
  timeAgo: string;
  linkUrl: string;
}

export const activityFeed: ActivityFeedItem[] = [
  { id: 1, text: '<strong>La Mexicana FM</strong> acaba de ganar la Batalla del Norte', icon: 'ri-trophy-line', iconColor: 'text-amber-500', timeAgo: 'hace 12 min', linkUrl: '/competencias' },
  { id: 2, text: '<strong>Radio Deportiva Total</strong> inició transmisión con 4,820 oyentes', icon: 'ri-radio-line', iconColor: 'text-rose-500', timeAgo: 'hace 18 min', linkUrl: '/radios-en-vivo' },
  { id: 3, text: '<strong>Ricky Navarro</strong> recibió 2,140 likes en su última batalla', icon: 'ri-heart-line', iconColor: 'text-red-500', timeAgo: 'hace 25 min', linkUrl: '/competencias' },
  { id: 4, text: '<strong>Banda MX Army</strong> alcanzó 14,500 miembros', icon: 'ri-vip-crown-line', iconColor: 'text-purple-500', timeAgo: 'hace 40 min', linkUrl: '/clubes' },
  { id: 5, text: '<strong>Festival Sonoro RFW</strong> ya tiene 9,800 menciones sociales', icon: 'ri-fire-line', iconColor: 'text-orange-500', timeAgo: 'hace 1 hora', linkUrl: '/eventos' },
  { id: 6, text: '<strong>Tardes con Vale</strong> lidera el ranking de programas esta semana', icon: 'ri-bar-chart-line', iconColor: 'text-emerald-500', timeAgo: 'hace 2 horas', linkUrl: '/rankings' },
];